<div class="page-header">
    <div>
        <h4 class="section-title">Automations</h4>
        <p class="page-subtitle">
            Timed tasks the agent runs by itself: a schedule, a task written as a prompt and the tools it may use. Each run is
            kept in the run history and as an "Automation" conversation. Read-only here: the customer creates, switches and runs
            them in the agents Dashboard.
        </p>
    </div>
</div>

<div class="info-note" role="note" *ngIf="planOff">
    <i class="fa-solid fa-circle-info"></i>
    <span>The project plan does not include automations: no trigger of this agent runs. Allow "Automations" on the Project plans page.</span>
</div>

<p class="muted triggers-count" *ngIf="triggers.length || planMax !== null">
    {{ triggers.length }} automation{{ triggers.length === 1 ? '' : 's' }}, {{ enabledCount }} on<span *ngIf="planMax !== null"> · plan allows {{ planMax }} per agent</span><span *ngIf="planRunsPerMonth !== null"> and {{ planRunsPerMonth }} runs per month for the project</span>
</p>
<div *ngIf="loading && triggers.length === 0" class="muted">Loading...</div>
<div *ngIf="error" class="error-text" role="alert">{{ error }}</div>
<div *ngIf="!loading && !error && triggers.length === 0" class="no-data" id="ag-triggers-none">
    <i class="fa-solid fa-clock"></i> This agent has no automations
</div>

<div class="trigger-box" *ngFor="let t of triggers" [attr.id]="'ag-trigger-' + t.id">
    <div class="trigger-head">
        <strong dir="auto">{{ t.name }}</strong>
        <span class="badge" [ngClass]="t.enabled ? 'ok' : 'off'">{{ t.enabled ? 'On' : 'Off' }}</span>
        <span class="badge draft">{{ t.type === 'TIMER' ? 'Timer' : t.type }}</span>
        <span class="badge" [ngClass]="runStatus(t.lastStatus).css" title="Last run">Last: {{ runStatus(t.lastStatus).label }}</span>
        <span class="badge warn" *ngIf="t.failuresInRow > 0" title="3 failed runs in a row switch the trigger off">{{ t.failuresInRow }} failed in a row</span>
    </div>
    <p class="muted" *ngIf="triggerOffReason(t, plan)">{{ triggerOffReason(t, plan) }}</p>

    <dl class="trigger-facts">
        <dt>Schedule</dt>
        <dd>{{ triggerScheduleText(t) }} <span class="muted">({{ t.timeZone || 'project time zone' }})</span></dd>
        <dt>Next run</dt>
        <dd>
            <ng-container *ngIf="t.nextRunLocal || t.nextRunAt; else noNext">
                {{ t.nextRunLocal || (t.nextRunAt | date: 'medium') }}
                <span class="muted mono" *ngIf="t.nextRunAt">{{ t.nextRunAt }} UTC</span>
            </ng-container>
            <ng-template #noNext>-</ng-template>
        </dd>
        <dt>Last run</dt>
        <dd>{{ t.lastRunAt ? (t.lastRunAt | date: 'medium') : '-' }}</dd>
        <dt>Task</dt>
        <dd><div class="trigger-prompt" dir="auto">{{ t.prompt || '-' }}</div></dd>
        <dt>Tools</dt>
        <dd>
            <span *ngIf="!t.allowedTools.length" class="muted">No tools</span>
            <span class="trigger-tools" *ngIf="t.allowedTools.length">
                <span
                    class="badge"
                    *ngFor="let name of t.allowedTools"
                    [ngClass]="t.preapprovedWrites.includes(name) ? 'warn' : 'draft'"
                    [title]="t.preapprovedWrites.includes(name) ? 'May do this without asking (pre-approved write)' : name"
                >
                    <i class="fa-solid fa-screwdriver-wrench"></i> {{ triggerToolLabel(name) }}<span *ngIf="t.preapprovedWrites.includes(name)"> · without asking</span>
                </span>
            </span>
        </dd>
        <dt>Result goes to</dt>
        <dd>{{ (t.output?.kind || 'DASHBOARD') === 'DASHBOARD' ? 'Dashboard (run history)' : t.output?.kind }}</dd>
    </dl>

    <button type="button" class="btn-light" [attr.id]="'ag-trigger-runs-' + t.id" (click)="toggleRuns(t)">
        <i class="fa-solid" [ngClass]="isOpen(t) ? 'fa-chevron-up' : 'fa-clock-rotate-left'"></i>
        {{ isOpen(t) ? 'Hide runs' : 'Show runs' }}
    </button>

    <div class="trigger-runs" *ngIf="isOpen(t)">
        <div *ngIf="runs[t.id].error" class="error-text" role="alert">{{ runs[t.id].error }}</div>
        <div *ngIf="!runs[t.id].loading && !runs[t.id].error && runs[t.id].items.length === 0" class="no-data">
            <i class="fa-solid fa-clock-rotate-left"></i> No runs yet
        </div>
        <div class="table-wrapper table-mobile-card" *ngIf="runs[t.id].items.length">
            <table class="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Scheduled for</th>
                        <th>Started</th>
                        <th>Status</th>
                        <th>Tokens in / out</th>
                        <th>Answer</th>
                        <th>Conversation</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let r of runs[t.id].items">
                        <td data-label="#">{{ r.seq }} <span class="badge draft" *ngIf="r.manual" title="Started with Run now">Run now</span></td>
                        <td data-label="Scheduled for">{{ r.scheduledFor ? (r.scheduledFor | date: 'medium') : '-' }}</td>
                        <td data-label="Started">
                            {{ r.startedAt ? (r.startedAt | date: 'medium') : '-' }}
                            <span class="muted" *ngIf="runSeconds(r) !== null"> · {{ runSeconds(r) }} s</span>
                        </td>
                        <td data-label="Status"><span class="badge" [ngClass]="runStatus(r.status).css">{{ runStatus(r.status).label }}</span></td>
                        <td data-label="Tokens in / out">{{ r.inputTokens ?? '-' }} / {{ r.outputTokens ?? '-' }}</td>
                        <td data-label="Answer">
                            <div *ngIf="r.error" class="error-text" dir="auto">{{ r.error }}</div>
                            <div *ngIf="r.outputText" class="run-answer" dir="auto" [class.open]="expanded.has(r.id)">{{ r.outputText }}</div>
                            <button type="button" class="link-btn" *ngIf="r.outputText && r.outputText.length > 160" (click)="toggleAnswer(r)">
                                {{ expanded.has(r.id) ? 'Less' : 'Full answer' }}
                            </button>
                            <span *ngIf="!r.outputText && !r.error" class="muted">-</span>
                        </td>
                        <td data-label="Conversation">
                            <a *ngIf="r.conversationId" [routerLink]="['/ai-agents/conversations', r.conversationId]">Open</a>
                            <span *ngIf="!r.conversationId" class="muted">-</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div *ngIf="runs[t.id].loading" class="muted">Loading...</div>
        <button type="button" class="btn-light" *ngIf="hasMoreRuns(t)" (click)="loadRuns(t)">
            More runs ({{ runs[t.id].items.length }} of {{ runs[t.id].total }})
        </button>
    </div>
</div>
